<template>
  <div
    class="d-flex justify-content-center"
    v-if="componentReady && hasModulePermission('check_ins')"
  >
    <div class="floating-action-buttons">
      <div class="row">
        <div class="col-12 justify-content-center text-center">
          <div class="btn-group align-items-center" role="group">
            <button
              type="button"
              class="
                btn
                fab-secondary
                d-flex
                justify-content-between
                flex-column
                align-items-center
              "
              style="margin-right: -12px"
              @click="updateAvailability('away')"
              :disabled="disableAwayBtn"
              :class="{ 'btn-wfh-secondary-hide': enableContinue }"
            >
              <i class="fas fa-running fa-2x"></i>
              <span>Away</span>
            </button>
            <button
              type="button"
              class="
                btn
                fab-primary
                d-flex
                justify-content-center
                flex-column
                align-items-center
              "
              @click="updateAvailability(btnPrimaryStatus)"
              :disabled="loading || hasSelfieCheckinError"
              :class="{
                'btn-wfh-primary-continue': enableContinue,
              }"
            >
              <img v-if="loading" :src="'/images/loader.svg'" class="btn-img" />
              <i
                v-else
                class="fas fa-2x"
                :class="{
                  'fa-play': displayPlayIcon,
                  'fa-pause': displayPauseIcon,
                }"
              ></i>
              {{ btnWfhPrimaryText }}
            </button>
            <button
              type="button"
              class="
                btn
                fab-secondary
                d-flex
                justify-content-between
                flex-column
                align-items-center
              "
              style="margin-left: -12px"
              @click="updateAvailability('check_out')"
              :disabled="disableCheckoutBtn"
            >
              <i class="fas fa-stop fa-2x"></i>
              <span>Checkout</span>
            </button>
          </div>
          <p class="text-danger mt-2" v-if="hasSelfieCheckinError">
            {{ selfieCheckinErrorMsg }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash"
import { detect } from 'detect-browser'

export default {
  props: ['photoCheckin', 'isDashboard'],

  data() {
    return {
      userAvailability: "",
      loading: false,
      browserName: null,
      browserOs: null,
    }
  },

  computed: {
    enableContinue() {
      return _.includes(["away", "break"], this.userAvailability)
    },

    btnWfhPrimaryText() {
      if (this.loading) return ""
      if (this.enableContinue) return "Continue"
      if (this.userAvailability === 'check_out') {
        return (this.photoCheckin ? 'Selfie ' : '') + 'Check-In'
      }
      if (!this.userAvailability) {
        return (this.photoCheckin ? 'Selfie ' : '') + 'Check-In'
      }
      return "Break"
    },

    btnPrimaryStatus() {
      return (this.userAvailability === 'check_out' || !this.userAvailability) ? 'check_in' :
        this.enableContinue ? 'continue' :
          'break'
    },

    displayPlayIcon() {
      return _.includes(["away", "break", "check_out", ""], this.userAvailability)
    },

    displayPauseIcon() {
      return _.includes(["continue", "check_in"], this.userAvailability)
    },

    disableAwayBtn() {
      return this.loading || _.includes(["check_out", "away", ""], this.userAvailability)
    },

    disableCheckoutBtn() {
      return this.loading || _.includes(["check_out", ""], this.userAvailability)
    },

    componentReady() {
      return this.isDashboard || (this.photoCheckin !== null && typeof this.photoCheckin !== 'undefined')
    },

    hasSelfieCheckinError() {
      return this.photoCheckin && this.browserOs === 'iOS' && (this.browserName === 'fxios' || this.browserName === 'crios')
    },

    selfieCheckinErrorMsg() {
      return 'Please use Safari browser on iOS for Selfie Check-in.'
    },
  },

  methods: {
    hasModulePermission(permission) {
      return hasPermission(permission);
    },

    async updateAvailability(status) {
      if (this.isDashboard && this.photoCheckin && status === 'check_in') {
        window.location.href = route('user-availability.check-in', {step: 2});
        return;
      }

      try {
        if (this.photoCheckin && status === 'check_in') {
          this.$emit('doPhotoCheckin')
          return
        }

        this.loading = true

        await axios.post(route('status.change'), { status })
        this.userAvailability = status

        this.loading = false
      } catch (e) {
        this.loading = false
      }
    },

    async fetchUserAvailability() {
      const res = await axios.get("/user-current-availability")

      this.userAvailability = res.data.data ? res.data.data.status : 'check_out'
    },
  },

  mounted() {
    this.fetchUserAvailability()
    const browser = detect()

    this.browserName = browser.name
    this.browserOs = browser.os
  }
}
</script>

<style scoped lang="scss">
@import "../../../sass/core/mixins";

.floating-action-buttons {
  z-index: 999;
  position: fixed;
  bottom: 72px;

  @include media-breakpoint-down(sm) {
    bottom: 88px;
  }
}

.fab-primary {
  background-color: #3592ff;
  border-radius: 50% !important;
  height: 104px;
  width: 104px;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.1);
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
  z-index: 2 !important;
  opacity: 1 !important;

  &:disabled {
    cursor: default;
    background-color: #79b1f3;

    i,
    span {
      opacity: 0.2;
    }
  }
}

.fab-secondary {
  height: 64px;
  width: 100px;
  border-radius: 40px;
  background-color: #ffffff;
  box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.1);
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
  text-align: center;
  z-index: 1 !important;
  opacity: 1 !important;

  &:disabled {
    cursor: default;

    i,
    span {
      opacity: 0.2;
    }
  }
}
</style>
